// src/pos/print.ts
import { invoke } from "@tauri-apps/api/core";
import iconv from "iconv-lite"; // + dodaj dependency u package.json

export type SlipItem = { name: string; qty: number; price: number };
export type OrderTicketItem = { name: string; qty: number; modifiers?: string[]; note?: string | null };
export type PrintTarget = { printerIp?: string; printerShare?: string };
const enc852 = (s: string) => new Uint8Array(iconv.encode(s, "cp852"));
// ---- binary-safe base64 ----
function b64FromBytes(u8: Uint8Array): string {
  if (typeof window === "undefined") return Buffer.from(u8).toString("base64");
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin);
}
const enc = (s: string) => new TextEncoder().encode(s);

// TAURI v2 backend u tvom kodu prima snake_case: data_b64, printer_ip, printer_share
async function sendToPrinter(bytes: Uint8Array, target: PrintTarget) {
  await invoke("print_raw", {
    dataB64: b64FromBytes(bytes),
    printerIp: target.printerIp || null,
    printerShare: target.printerShare || null,
  });
}
const isEscPos = (t: PrintTarget) => !!(t.printerIp || t.printerShare);

// ================== ESC/POS builder ==================
class EscPos {
  private parts: number[] = [];
  private push(...n: number[]) { this.parts.push(...n); }
  codepage(n: number) { this.push(0x1B, 0x74, n); return this; } // ESC t n
  txt(s: string) { enc852(s).forEach(b => this.parts.push(b)); return this; }
  init() { this.push(0x1B,0x40); return this; }                          // ESC @
  align(a: "L"|"C"|"R") { this.push(0x1B,0x61, a==="L"?0:a==="C"?1:2); return this; } // ESC a
  bold(on = true) { this.push(0x1B,0x45,on?1:0); return this; }          // ESC E
  underline(on = true) { this.push(0x1B,0x2D,on?1:0); return this; }     // ESC -
  dblSize(w=true,h=true){ let n=0; if(w)n|=0x20; if(h)n|=0x10; this.push(0x1D,0x21,n); return this; } // GS !
  //create triple and 4x size if is possible
  quadSize(w=true,h=true){ let n=0; if(w)n|=0x40; if(h)n|=0x30; this.push(0x1D,0x21,n); return this; } // GS !
  tripleSize(w=true,h=true){ let n=0; if(w)n|=0x30; if(h)n|=0x20; this.push(0x1D,0x21,n); return this; } // GS !
  feed(n=1) { this.push(0x1B,0x64,n); return this; }                     // ESC d
  hr(w=42) { this.txt("-".repeat(w) + "\n"); return this; }
  cut() { this.push(0x1D,0x56,0x00); return this; }                      // GS V 0
  qr(data: string, size=6, ecc: "L"|"M"|"Q"|"H"="M") {
    // model 2
    this.push(0x1D,0x28,0x6B, 0x04,0x00, 0x31,0x41,0x32,0x00);
    // size 3..16
    const s = Math.max(3, Math.min(16, size));
    this.push(0x1D,0x28,0x6B, 0x03,0x00, 0x31,0x43, s);
    // ECC
    const eccByte = {L:48,M:49,Q:50,H:51}[ecc];
    this.push(0x1D,0x28,0x6B, 0x03,0x00, 0x31,0x45, eccByte);
    // store
    const d = enc(data);
    const len = d.length + 3, pL = len & 0xff, pH = (len>>8)&0xff;
    this.push(0x1D,0x28,0x6B, pL,pH, 0x31,0x50,0x30); d.forEach(b=>this.parts.push(b));
    // print
    this.push(0x1D,0x28,0x6B, 0x03,0x00, 0x31,0x51,0x30);
    this.feed(1);
    return this;
  }
  build() { return new Uint8Array(this.parts); }
}

// ================== KITCHEN READY (styled) ==================
export async function printKitchenReadySlipStyled(
  header: {
    orderId: string | number;
    tableLabel?: string | number | null;
    title?: string; // npr. "SPREMNO"
    when?: Date;
  },
  items: OrderTicketItem[],
  target: PrintTarget
) {
  const code = String(header.orderId).padStart(4, "0");
  const when = (header.when || new Date()).toLocaleString();

  if (isEscPos(target)) {
    const p = new EscPos()
      .init()
      .codepage(18) // CP852
      .align("C")
      .bold(true)
      .tripleSize(true, true)
      .txt(`#${code}\n`)
      .bold(true)
      .tripleSize(false, false)
      .txt(`${header.title || "SPREMNO"}\n`)
      .bold(false)
      .txt(`${when}\n`);

    if (header.tableLabel) p.txt(`Sto: ${header.tableLabel}\n`);

    p.hr();

    for (const it of items) {
      p.bold(true).txt(`${it.qty} x ${it.name}\n`).bold(false);
      if (it.modifiers?.length) {
        for (const m of it.modifiers) if (m) p.txt(`  • ${m}\n`);
      }
      if (it.note && String(it.note).trim()) {
        p.underline(true).txt(`  ✎ ${String(it.note).trim()}\n`).underline(false);
      }
      p.feed(1);
    }
      p.underline(true);
  p.hr().feed(1).underline(false);
  p.hr().feed(1);
  p.hr().feed(2);

  
    p.cut();

    await sendToPrinter(p.build(), target);
    return;
  }

  // Plain text za share/driver (bez CUT)
  const txt: string[] = [];
  txt.push(`==== ORDER #${code} ====`);
  txt.push(header.title || "SPREMNO");
  txt.push(when);
  if (header.tableLabel) txt.push(`Sto: ${header.tableLabel}`);
  txt.push("----------------------------------------");
  for (const it of items) {
    txt.push(`${it.qty} × ${it.name}`);
    if (it.modifiers?.length) for (const m of it.modifiers) txt.push(`  • ${m}`);
    if (it.note && String(it.note).trim()) txt.push(`  ✎ ${String(it.note).trim()}`);
    txt.push("");
  }
  txt.push("----------------------------------------");
  txt.push("\n\n\n");
  await sendToPrinter(enc(txt.join("\n")), target);
}


// ================== STYLED ORDER TICKET ==================
export async function printOrderTicketStyled(
  header: {
    clientName: string;
    orderId: string|number;
    fiscalNumber?: string; // npr. "F123/2024"
    workerName?: string;
    tableLabel?: string;
    verifyBaseUrl?: string; // npr. https://app.itable.app/order
  },
  items: OrderTicketItem[],
  target: PrintTarget
) {
  const code = String(header.orderId).padStart(4,"0");
  const when = new Date().toLocaleString();
  const verifyUrl = (header.verifyBaseUrl || "https://portal.itable.app/order-check").replace(/\/$/,"") + `/${encodeURIComponent(code)}`;

  if (isEscPos(target)) {
     const p = new EscPos().init().codepage(18) // 18 = CP852 (Slavic)
  .align("C").bold(true).tripleSize(true,true).txt(`#${code}\n`)

 p.feed(3);
      p.bold(true).tripleSize(false,false).txt(`${header.clientName}\n`).bold(false)
      .txt(`${when}\n`);
     p.feed(3);
    if (header.workerName) p.txt(`Radnik: ${header.workerName}\n`);
    if (header.tableLabel) p.txt(`Sto: ${header.tableLabel}\n`);
    p.hr();
    for (const it of items) {
      p.align("L");
  p.bold(true).txt(`${it.qty} x ${it.name}\n`).bold(false); // × -> x
  p.align("R");
      if (it.modifiers?.length) for (const m of it.modifiers) if (m) p.txt(`- ${m}\n`);
      if (it.note && String(it.note).trim()) p.underline(true).txt(`-> ${String(it.note).trim()}\n`).underline(false);
      p.feed(1);
    }
    p.align("C");
    p.hr().feed(1);
    p.align("C").txt("Provjeri narudžbu:\n").qr(verifyUrl, 6, "M").txt(`${verifyUrl}\n`);
    p.hr().feed(2);
    p.hr().feed(1);
    p.feed(3);
    //add text da ovo nije fiskalni racun i da se fiskalni racun moze preuzeti na pultu kod radnika
     p.align("L").txt("Napomena: Ovo nije fiskalni račun.\nFiskalni račun možete preuzeti na šanku.\n");
    p.hr()
    p.feed(2);
    p.cut();
    await sendToPrinter(p.build(), target);
    return;
  }

  // Fallback tekst za SHARE/driver (ako ESC/POS nije propušten)
  const txt: string[] = [];
  txt.push(`==== ORDER #${code} ====`);
  txt.push(header.clientName);
  txt.push(when);
  if (header.workerName) txt.push(`Radnik: ${header.workerName}`);
  if (header.tableLabel) txt.push(`Sto: ${header.tableLabel}`);
  txt.push("----------------------------------------");
  for (const it of items) {
    txt.push(`${it.qty} × ${it.name}`);
    if (it.modifiers?.length) for (const m of it.modifiers) txt.push(`  • ${m}`);
    if (it.note && String(it.note).trim()) txt.push(`  ✎ ${String(it.note).trim()}`);
    txt.push("");
  }
  txt.push("----------------------------------------");
  txt.push("Provjeri narudžbu:");
  txt.push(verifyUrl);
  txt.push("\n\n\n");
  await sendToPrinter(enc(txt.join("\n")), target);
}

// ================== DEMO (iTable APP) ==================
export async function printDemoOrderTicketStyled(target: PrintTarget) {
  const demoItems: OrderTicketItem[] = [
    { name: "Ćevapi (10)", qty: 1, modifiers: ["Luk", "Kajmak +2"], note: "Bez soli" },
    { name: "Pomfrit", qty: 2, modifiers: ["Cheddar"] },
    { name: "Coca-Cola 0.5", qty: 2 },
  ];
  await printOrderTicketStyled(
    {
      clientName: "iTable APP",
      orderId: 7,
      workerName: "Demo",
      tableLabel: "T1",
      verifyBaseUrl: "https://portal.itable.app/order-check",
    },
    demoItems,
    target
  );
}

// ================== Post-fiscal slip (tvoj postojeći, samo snake_case invoke & cut za 9100) ==================
function withEscPosCutIf9100(payload: string, target: PrintTarget) {
  //eslint-disable-next-line
 return (target.printerIp || target.printerShare) ? (payload + "\n\n\n\n\n\n" + "\x1D\x56\x00"): payload;}
function buildOrderSlip(orderCode: string, items: SlipItem[]) {
  const lines: string[] = [];
  lines.push("*** ORDER " + orderCode + " ***");
  lines.push(new Date().toLocaleString());
  lines.push("----------------------------");
  for (const it of items) {
    const total = (Number(it.qty)||0) * (Number(it.price)||0);
    lines.push(`${it.qty} x ${it.name}`);
    lines.push(`   @ ${it.price.toFixed(2)} = ${total.toFixed(2)}`);
  }
  lines.push("----------------------------");
  lines.push("Hvala! iTable");
  lines.push("\n\n\n\n\n\n");
  return lines.join("\n");
}
function toBase64(s: string) {
  if (typeof window === "undefined") return Buffer.from(s,"utf8").toString("base64");
  // utf8-safe
  const utf8 = new TextEncoder().encode(s);
  return b64FromBytes(utf8);
}
export async function printAfterFiscal(orderCode: string, items: SlipItem[], target: PrintTarget) {
  const payload = withEscPosCutIf9100(buildOrderSlip(orderCode, items), target);
  await invoke("print_raw", {
    dataB64: toBase64(payload),
    printerIp: target.printerIp || null,
    printerShare: target.printerShare || null,
  });
}
